/**
 * Public blog article page for slug-based post routes.
 *
 * What: Resolves `/blog/:slug`, loads mapped MDX source text, and renders article content with related posts.
 * Why: Gives each post a stable canonical route and supports deeper internal-link navigation from the blog hub.
 * When: Update when slug resolution, article rendering, related-post behavior, or content-loading strategy changes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { getBlogTuitionSubject } from '@shared/blog/blogCategories.js';
import BlogArticleCta, {
  BLOG_ARTICLE_CTA_SECTION_ID,
} from '@/components/blog/BlogArticleCta';
import { UPDATED_COLORS } from '@/components/blog/blogCardConstants';
import BlogShareButtons from '@/components/blog/BlogShareButtons';
import BlogTldrCallout from '@/components/blog/BlogTldrCallout';
import { pushablePrimarySx } from '@/components/landing/landingButtons';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingMobileNav from '@/components/landing/LandingMobileNav';
import { landingRevealStyles } from '@/components/landing/landingRevealStyles';
import LandingSectionShell from '@/components/landing/LandingSectionShell';
import {
  colors,
  radii,
  sectionSpacing,
} from '@/components/landing/landingTokens';
import LandingTopBar from '@/components/landing/LandingTopBar';
import ScrollProgressBar from '@/components/landing/ScrollProgressBar';
import { getBrandingConfig } from '@/config/runtimeBrandConfig';
import {
  getPreloadedBlogPostContent,
  loadRawBlogPostContent,
} from '@/content/blog/blogPostContentLoader';
import type { BlogPostRecord } from '@/content/blog/blogPostRepository';
import {
  getBlogPostBySlug,
  listRelatedBlogPosts,
} from '@/content/blog/blogPostRepository';
import { TuitionSubjectProvider } from '@/contexts/tuitionSubjectContext';
import { useAuth } from '@/hooks/useAuth';
import { useSectionFadeIn } from '@/hooks/useSectionFadeIn';
import { getDefaultRedirectPath } from '@/utils/accessControl';
import type {
  BlogArticleSeoMetadata,
  BlogPostInput,
} from '@/utils/blogSeoMetadata';
import {
  buildBlogArticleSeoMetadata,
  parseFaqPairsFromMarkdown,
} from '@/utils/blogSeoMetadata';
import { extractTldrFromMarkdown } from '@/utils/blogTldrParser';
import { formatBlogDate, isPostUpdated } from '@/utils/dateFormatting';
import { typesetMarkdown } from '@/utils/markdownTypeset';
import { trackMetaEvent } from '@/utils/metaPixel.js';
import {
  getReadingTimeMinutesFromMarkdown,
  stripFrontmatter,
} from '@/utils/readingTime';
import { rehypeHeadingSiblings } from '@/utils/rehypeHeadingSiblings';

/** Reading-optimized width: ~65–70 characters per line (publication standard). */
const ARTICLE_READING_WIDTH = '42rem'; // ~672px
/** Related-posts section: `related` resolves it to the site's exploration reveal. */
const RELATED_SECTION_ID = 'blog-article-related';
const RELATED_TITLE_ID = 'blog-article-related-title';
/** Serif stack for article body only; system fonts for zero extra weight. */
const ARTICLE_BODY_FONT =
  'Georgia, "Times New Roman", "Liberation Serif", serif';

function getSafeMarkdownHref(href: unknown): string | null {
  if (typeof href !== 'string') {
    return null;
  }

  const trimmedHref = href.trim();
  if (!trimmedHref) {
    return null;
  }

  const normalizedHref = Array.from(trimmedHref)
    .filter(char => {
      const code = char.charCodeAt(0);
      return !(code <= 32 || code === 127);
    })
    .join('')
    .toLowerCase();

  if (
    normalizedHref.startsWith('javascript:') ||
    normalizedHref.startsWith('data:') ||
    normalizedHref.startsWith('vbscript:')
  ) {
    return null;
  }

  if (trimmedHref.startsWith('//')) {
    return null;
  }

  if (
    trimmedHref.startsWith('/') ||
    trimmedHref.startsWith('#') ||
    /^https?:\/\//i.test(trimmedHref) ||
    /^mailto:/i.test(trimmedHref) ||
    /^tel:/i.test(trimmedHref)
  ) {
    return trimmedHref;
  }

  return null;
}

function isInternalMarkdownHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function MarkdownLink({
  children,
  href,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  const safeHref = getSafeMarkdownHref(href);

  if (!safeHref) {
    return <Typography component="span">{children}</Typography>;
  }

  if (isInternalMarkdownHref(safeHref)) {
    return (
      <Link
        {...rest}
        component={RouterLink}
        to={safeHref}
        underline="always"
        sx={{ color: colors.accentOrange, fontWeight: 600 }}
      >
        {children}
      </Link>
    );
  }

  if (safeHref.startsWith('#')) {
    return (
      <Link
        {...rest}
        href={safeHref}
        underline="always"
        sx={{ color: colors.accentOrange, fontWeight: 600 }}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      {...rest}
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      sx={{ color: colors.accentOrange, fontWeight: 600 }}
    >
      {children}
    </Link>
  );
}

/**
 * Heading top-margin is set per-component using the `data-after-p` attribute
 * injected by rehypeHeadingSiblings. When a heading immediately follows a
 * paragraph the "section gap" is larger; otherwise a standard gap is used.
 * This is deterministic across renders and page refreshes because the spacing
 * decision lives in the AST, not in CSS sibling selectors.
 */
function isHeadingAfterParagraph(
  node: Record<string, unknown> | undefined,
  dataAfterPAttr: unknown,
  dataAfterPCamel: unknown
): boolean {
  return Boolean(
    (node as { data?: { afterParagraph?: boolean } })?.data?.afterParagraph ||
      dataAfterPAttr === 'true' ||
      dataAfterPAttr === true ||
      dataAfterPCamel === 'true' ||
      dataAfterPCamel === true
  );
}

// react-markdown custom-renderer prop shape. The `node` prop is a hast element
// (Record is a safe structural stand-in) and the rest are HTML-style attributes
// forwarded to the underlying element — typing them as `unknown` keeps the
// spread-through safe without pulling in hast types.
interface MarkdownRendererProps {
  node?: Record<string, unknown>;
  children?: React.ReactNode;
  [key: string]: unknown;
}

const MARKDOWN_COMPONENTS: Record<string, React.FC<MarkdownRendererProps>> = {
  h1: () => null,
  h2: ({
    node,
    'data-after-p': afterPAttr,
    dataAfterP,
    ...props
  }: MarkdownRendererProps) => {
    const afterP = isHeadingAfterParagraph(node, afterPAttr, dataAfterP);
    return (
      <Typography
        variant="h2"
        sx={{
          fontFamily: 'inherit',
          fontSize: { xs: '1.375rem', sm: '1.5rem' },
          fontWeight: 700,
          lineHeight: 1.3,
          letterSpacing: '-0.02em',
          color: colors.ink,
          mt: afterP ? { xs: 4, sm: 5 } : { xs: 3.5, sm: 4.5 },
          mb: { xs: 1.25, sm: 1.5 },
          scrollMarginTop: { xs: 100, md: 120 },
        }}
        {...props}
      />
    );
  },
  h3: ({
    node,
    'data-after-p': afterPAttr,
    dataAfterP,
    ...props
  }: MarkdownRendererProps) => {
    const afterP = isHeadingAfterParagraph(node, afterPAttr, dataAfterP);
    return (
      <Typography
        variant="h3"
        sx={{
          fontFamily: 'inherit',
          fontSize: { xs: '1.1875rem', sm: '1.25rem' },
          fontWeight: 600,
          lineHeight: 1.35,
          color: colors.ink,
          mt: afterP ? { xs: 3.25, sm: 4 } : { xs: 3, sm: 3.5 },
          mb: { xs: 1, sm: 1.25 },
          scrollMarginTop: { xs: 100, md: 120 },
        }}
        {...props}
      />
    );
  },
  p: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Typography
      component="p"
      sx={{
        fontFamily: ARTICLE_BODY_FONT,
        fontSize: { xs: '1.0625rem', sm: '1.125rem' },
        lineHeight: 1.8,
        color: colors.inkMuted,
      }}
      {...props}
    />
  ),
  ul: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="ul"
      sx={{
        fontFamily: ARTICLE_BODY_FONT,
        pl: { xs: 2.25, sm: 2.5 },
        my: 1.75,
        color: colors.inkMuted,
        fontSize: { xs: '1.0625rem', sm: '1.125rem' },
      }}
      {...props}
    />
  ),
  ol: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="ol"
      sx={{
        fontFamily: ARTICLE_BODY_FONT,
        pl: { xs: 2.5, sm: 2.75 },
        my: 1.75,
        color: colors.inkMuted,
        fontSize: { xs: '1.0625rem', sm: '1.125rem' },
      }}
      {...props}
    />
  ),
  li: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="li"
      sx={{
        mb: 0.75,
        lineHeight: 1.8,
        '& > p': {
          display: 'inline',
          fontSize: 'inherit',
          lineHeight: 1.8,
        },
        '&::marker': {
          color: colors.inkSubtle,
        },
        '&:last-child': {
          mb: 0,
        },
      }}
      {...props}
    />
  ),
  blockquote: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="blockquote"
      sx={{
        fontFamily: ARTICLE_BODY_FONT,
        my: 2.5,
        mx: 0,
        py: 1.25,
        pl: 2,
        pr: 0,
        borderLeft: `4px solid ${colors.accentCobalt}`,
        backgroundColor: colors.backgroundAlt,
        color: colors.inkMuted,
        fontSize: { xs: '1.0625rem', sm: '1.125rem' },
        lineHeight: 1.75,
        fontStyle: 'italic',
        '& > p': {
          fontStyle: 'italic',
        },
      }}
      {...props}
    />
  ),
  hr: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="hr"
      sx={{
        border: 0,
        borderTop: `1px solid ${colors.border}`,
        my: { xs: 3.5, sm: 4 },
      }}
      {...props}
    />
  ),
  table: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        my: 2.5,
      }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: 460,
          borderCollapse: 'separate',
          borderSpacing: 0,
          border: `1px solid ${colors.border}`,
          borderRadius: 1.5,
          backgroundColor: colors.paper,
        }}
        {...props}
      />
    </Box>
  ),
  th: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="th"
      sx={{
        textAlign: 'left',
        fontWeight: 700,
        color: colors.ink,
        fontSize: '0.92rem',
        px: 1.5,
        py: 1,
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.backgroundAlt,
      }}
      {...props}
    />
  ),
  td: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="td"
      sx={{
        color: colors.inkMuted,
        fontSize: '0.95rem',
        lineHeight: 1.7,
        px: 1.5,
        py: 1,
        borderBottom: `1px solid ${colors.border}`,
      }}
      {...props}
    />
  ),
  tr: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="tr"
      sx={{
        '&:last-child td': {
          borderBottom: 0,
        },
      }}
      {...props}
    />
  ),
  pre: ({ node: _node, ...props }: MarkdownRendererProps) => (
    <Box
      component="pre"
      sx={{
        my: 2.5,
        mx: 0,
        p: 0,
        backgroundColor: 'transparent',
      }}
      {...props}
    />
  ),
  code: ({
    node: _node,
    inline,
    className,
    children,
    ...props
  }: {
    node?: unknown;
    inline?: boolean;
    className?: string;
    children?: React.ReactNode;
    [key: string]: unknown;
  }) => {
    if (inline) {
      return (
        <Box
          component="code"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.9em',
            px: 0.5,
            py: 0.15,
            borderRadius: 0.5,
            color: colors.ink,
            backgroundColor: colors.backgroundAlt,
            border: `1px solid ${colors.border}`,
          }}
          {...props}
        >
          {children}
        </Box>
      );
    }

    return (
      <Box
        component="code"
        className={className}
        sx={{
          display: 'block',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
          lineHeight: 1.6,
          color: '#163A69',
          backgroundColor: '#F4F7FB',
          border: `1px solid ${colors.border}`,
          borderRadius: 1,
          px: 1.5,
          py: 1.25,
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
        {...props}
      >
        {children}
      </Box>
    );
  },
  a: MarkdownLink,
};

function BlogArticlePage() {
  const { loginWithRedirect } = useAuth0();
  const { isAuthenticated, userRole, isRoleLoaded } = useAuth();
  const navigate = useNavigate();
  const { slug = '' } = useParams();
  const branding = getBrandingConfig();
  const brandName = branding?.siteTitle || 'DeepThink';
  const socialImageUrl = branding?.ogImageUrl;
  const publisherLogoUrl = branding?.footerLogoUrl || branding?.logoUrl;
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://deepthink.sg';

  const blogPost = useMemo(
    () => getBlogPostBySlug(slug) as BlogPostRecord | null,
    [slug]
  );
  const relatedPosts = useMemo(
    () => (blogPost ? listRelatedBlogPosts(blogPost.slug, { limit: 3 }) : []),
    [blogPost]
  );
  const preloadedArticleContent = useMemo(
    () =>
      blogPost?.contentFilePath
        ? getPreloadedBlogPostContent(blogPost.contentFilePath)
        : '',
    [blogPost?.contentFilePath]
  );

  // Scroll reveals for the article's tail only. The reading column is
  // deliberately excluded — prose has to be legible the moment it paints, and
  // the body arrives asynchronously, so its ids don't exist on mount anyway.
  const revealSectionIds = useMemo(
    () =>
      blogPost
        ? [
            ...(relatedPosts.length > 0 ? [RELATED_SECTION_ID] : []),
            BLOG_ARTICLE_CTA_SECTION_ID,
          ]
        : [],
    [blogPost, relatedPosts.length]
  );
  useSectionFadeIn(revealSectionIds);

  const [articleContent, setArticleContent] = useState(preloadedArticleContent);
  const [isLoadingArticleContent, setIsLoadingArticleContent] = useState(
    Boolean(blogPost && !preloadedArticleContent)
  );
  const [hasArticleContentError, setHasArticleContentError] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Meta Pixel PageView for blog article attribution (CAPI + Pixel)
  useEffect(() => {
    trackMetaEvent('PageView');
  }, [slug]);

  // Depend only on slug (primitive) so the effect does not re-run when blogPost
  // reference changes; getBlogPostBySlug returns a new object each call.
  useEffect(() => {
    let isMounted = true;

    setArticleContent(preloadedArticleContent);
    setHasArticleContentError(false);

    const post = getBlogPostBySlug(slug) as BlogPostRecord | null;
    if (!post) {
      setIsLoadingArticleContent(false);
      return () => {
        isMounted = false;
      };
    }

    if (preloadedArticleContent) {
      setIsLoadingArticleContent(false);
      return () => {
        isMounted = false;
      };
    }

    setIsLoadingArticleContent(true);

    loadRawBlogPostContent(post.contentFilePath!)
      .then(rawText => {
        if (!isMounted) return;
        if (!rawText || typeof rawText !== 'string') {
          setHasArticleContentError(true);
          return;
        }
        setArticleContent(rawText);
      })
      .catch(() => {
        if (!isMounted) return;
        setHasArticleContentError(true);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingArticleContent(false);
      });

    return () => {
      isMounted = false;
    };
  }, [preloadedArticleContent, slug]);

  const typesetArticleMarkdown = useMemo(
    () => typesetMarkdown(stripFrontmatter(articleContent)),
    [articleContent]
  );

  const { tldr: articleTldr, strippedMarkdown: articleMarkdown } = useMemo(
    () => extractTldrFromMarkdown(typesetArticleMarkdown),
    [typesetArticleMarkdown]
  );

  const articleFaqPairs = useMemo(
    () => parseFaqPairsFromMarkdown(articleContent),
    [articleContent]
  );

  const articleSeoMetadata = useMemo<BlogArticleSeoMetadata | null>(() => {
    if (!blogPost) {
      return null;
    }

    return buildBlogArticleSeoMetadata({
      brandName,
      siteOrigin: origin,
      blogPost: blogPost as unknown as BlogPostInput,
      socialImageUrl,
      publisherLogoUrl,
      faqPairs: articleFaqPairs,
    });
  }, [
    blogPost,
    brandName,
    origin,
    socialImageUrl,
    publisherLogoUrl,
    articleFaqPairs,
  ]);

  const displayReadingMinutes = useMemo(() => {
    if (!blogPost) {
      return 0;
    }
    if (articleContent) {
      return getReadingTimeMinutesFromMarkdown(articleContent);
    }
    return blogPost.readingTimeMinutes;
  }, [blogPost, articleContent]);

  const handleLogin = () => {
    if (isAuthenticated && isRoleLoaded) {
      navigate(getDefaultRedirectPath(userRole), { replace: true });
      return;
    }

    loginWithRedirect();
  };

  if (!blogPost) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.background,
          position: 'relative',
        }}
      >
        <Helmet>
          <title>{`Article Not Found | ${brandName} Blog`}</title>
          <meta
            name="description"
            content="This blog article could not be found."
          />
          <meta name="robots" content="noindex,follow" />
        </Helmet>

        <ScrollProgressBar />
        <LandingTopBar
          onLogin={handleLogin}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <LandingMobileNav
          onLogin={handleLogin}
          brandName={brandName}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />

        <Box
          component="main"
          sx={{
            pt: { xs: 12, md: 14 },
            pb: sectionSpacing.py,
          }}
        >
          <Container maxWidth="md">
            <Card
              sx={{
                borderRadius: radii.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.paper,
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 4 }, overflow: 'visible' }}>
                <Stack spacing={2}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '1.6rem', sm: '2rem' },
                      fontWeight: 800,
                      color: colors.ink,
                      letterSpacing: -0.4,
                    }}
                  >
                    Article not found
                  </Typography>
                  <Typography sx={{ color: colors.inkMuted, lineHeight: 1.7 }}>
                    The article URL may be outdated, or the post may have been
                    unpublished.
                  </Typography>
                  <Box>
                    <Button
                      component={RouterLink}
                      to="/blog"
                      startIcon={<ArrowBackIcon />}
                      sx={pushablePrimarySx}
                    >
                      Back to Blog
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Container>
        </Box>

        <LandingFooter />
      </Box>
    );
  }

  // Non-null: blogPost was checked above, so articleSeoMetadata is always set here
  const seo = articleSeoMetadata!;
  const seoTitle = seo.title;
  const seoDescription = seo.description;
  const canonicalUrl = seo.canonicalUrl;

  return (
    <TuitionSubjectProvider value={getBlogTuitionSubject(blogPost.category)}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.background,
          position: 'relative',
          // Scroll-reveal animations — see landingRevealStyles.ts for the design
          // system (motion types, intensity tiers, section mapping).
          ...(landingRevealStyles as Record<string, unknown>),
        }}
      >
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:type" content={seo.openGraph.type} />
          <meta property="og:title" content={seo.openGraph.title} />
          <meta property="og:description" content={seo.openGraph.description} />
          <meta property="og:url" content={seo.openGraph.url} />
          <meta property="og:image" content={seo.openGraph.image} />
          <meta property="og:image:alt" content={seo.openGraph.imageAlt} />
          <meta property="og:site_name" content={seo.openGraph.siteName} />
          <meta property="og:locale" content={seo.openGraph.locale} />
          <meta
            property="article:published_time"
            content={seo.openGraph.articlePublishedTime}
          />
          <meta
            property="article:modified_time"
            content={seo.openGraph.articleModifiedTime}
          />
          <meta
            property="article:author"
            content={seo.openGraph.articleAuthor}
          />
          <meta
            property="article:section"
            content={seo.openGraph.articleSection}
          />
          {seo.openGraph.articleTags?.map(tag => (
            <meta
              key={`article-tag-${tag}`}
              property="article:tag"
              content={tag}
            />
          ))}
          <meta name="twitter:card" content={seo.twitter.card} />
          <meta name="twitter:title" content={seo.twitter.title} />
          <meta name="twitter:description" content={seo.twitter.description} />
          <meta name="twitter:image" content={seo.twitter.image} />
          <meta name="twitter:image:alt" content={seo.twitter.imageAlt} />
          <script type="application/ld+json">
            {JSON.stringify(seo.jsonLd.blogPosting)}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(seo.jsonLd.breadcrumb)}
          </script>
          {seo.jsonLd.faqPage ? (
            <script type="application/ld+json">
              {JSON.stringify(seo.jsonLd.faqPage)}
            </script>
          ) : null}
        </Helmet>

        <ScrollProgressBar />
        <LandingTopBar
          onLogin={handleLogin}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <LandingMobileNav
          onLogin={handleLogin}
          brandName={brandName}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />

        <Box
          component="main"
          sx={{
            pt: { xs: 11, md: 13 },
            pb: sectionSpacing.py,
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              px: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            {/* Single reading column: publication-style width and alignment */}
            <Box
              sx={{
                maxWidth: ARTICLE_READING_WIDTH,
                mx: 'auto',
              }}
            >
              <Stack spacing={0}>
                {/* Section link: editorial pattern (e.g. NYT, Atlantic) — subtle text, not a button */}
                <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
                  <Link
                    component={RouterLink}
                    to="/blog"
                    underline="none"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      color: colors.inkSubtle,
                      transition: 'color 0.15s ease',
                      '&:hover': {
                        color: colors.accentOrange,
                      },
                    }}
                  >
                    ‹ Blog
                  </Link>
                </Box>

                <Box
                  component="header"
                  sx={{
                    mb: { xs: 2.5, sm: 3 },
                    // When a TL;DR callout renders below, its tinted card is already a strong
                    // boundary — a horizontal rule here becomes visual noise. Drop the rule
                    // (and the padding that made room for it) when the callout takes over.
                    ...(articleTldr
                      ? {}
                      : {
                          borderBottom: `1px solid ${colors.border}`,
                          pb: { xs: 2, sm: 2.5 },
                        }),
                  }}
                >
                  <Chip
                    label={blogPost.category}
                    size="small"
                    sx={{
                      backgroundColor: colors.backgroundAlt,
                      color: colors.ink,
                      fontWeight: 700,
                      borderRadius: radii.pill,
                      mb: 1.5,
                    }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem' },
                      fontWeight: 800,
                      lineHeight: 1.2,
                      letterSpacing: '-0.03em',
                      color: colors.ink,
                      mb: 2,
                    }}
                  >
                    {blogPost.title}
                  </Typography>
                  {/* Deck/subtitle: sans-serif so it reads as header, not first paragraph */}
                  <Typography
                    component="p"
                    sx={{
                      fontFamily: 'inherit',
                      fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                      lineHeight: 1.7,
                      color: colors.inkMuted,
                      fontWeight: 400,
                      mb: 2,
                    }}
                  >
                    {seoDescription}
                  </Typography>
                  {/* Article byline — clean editorial style */}
                  <Box sx={{ mb: 2.5 }}>
                    {isPostUpdated(blogPost) ? (
                      <>
                        <Typography
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: UPDATED_COLORS.text,
                            fontSize: { xs: '0.8125rem', sm: '0.84rem' },
                            fontWeight: 600,
                            backgroundColor: UPDATED_COLORS.bg,
                            borderRadius: radii.pill,
                            px: 1.5,
                            py: 0.25,
                            mb: 1,
                          }}
                        >
                          {`Updated ${formatBlogDate(blogPost.updatedDate)}`}
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.inkMuted,
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                            mb: 0.5,
                          }}
                        >
                          {`By ${blogPost.authorName} · Originally published ${formatBlogDate(blogPost.publishDate)} · ${displayReadingMinutes} min read`}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          color: colors.inkMuted,
                          fontSize: { xs: '0.875rem', sm: '0.9rem' },
                          mb: 0.5,
                        }}
                      >
                        {`By ${blogPost.authorName} · ${formatBlogDate(blogPost.publishDate)} · ${displayReadingMinutes} min read`}
                      </Typography>
                    )}
                    {blogPost.reviewerCredentials && (
                      <Typography
                        sx={{
                          color: colors.inkSubtle,
                          fontSize: { xs: '0.8125rem', sm: '0.84rem' },
                          lineHeight: 1.55,
                        }}
                      >
                        {blogPost.reviewerCredentials}
                      </Typography>
                    )}
                  </Box>
                  <BlogShareButtons
                    url={canonicalUrl}
                    title={blogPost.title}
                    description={seoDescription}
                    variant="inline"
                  />
                </Box>

                {/* Article body: serif starts here; clear boundary from header */}
                <Box
                  sx={{
                    '& > *': { mb: { xs: 2, sm: 2.25 } },
                    '& > *:last-child': { mb: 0 },
                    '& p + p': { mt: 1.5 },
                    '& ul + p, & ol + p': { mt: 1.5 },
                    '& p + ul, & p + ol': { mt: 0.75 },
                  }}
                >
                  {isLoadingArticleContent && (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{
                        alignItems: 'center',
                        color: colors.inkMuted,
                        py: 3,
                      }}
                    >
                      <CircularProgress size={20} />
                      <Typography sx={{ fontSize: '1rem' }}>
                        Loading article...
                      </Typography>
                    </Stack>
                  )}

                  {hasArticleContentError && (
                    <Alert severity="warning" sx={{ my: 2 }}>
                      We could not load the full article body right now. Please
                      try again shortly.
                    </Alert>
                  )}

                  {!isLoadingArticleContent && !hasArticleContentError && (
                    <>
                      {articleTldr ? (
                        <BlogTldrCallout tldr={articleTldr} />
                      ) : null}
                      <ReactMarkdown
                        skipHtml
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeSanitize, rehypeHeadingSiblings]}
                        components={MARKDOWN_COMPONENTS}
                      >
                        {articleMarkdown}
                      </ReactMarkdown>
                    </>
                  )}
                </Box>

                {/* Share buttons — prominent block at end of article */}
                <Box sx={{ mt: { xs: 4, sm: 5 } }}>
                  <BlogShareButtons
                    url={canonicalUrl}
                    title={blogPost.title}
                    description={seoDescription}
                    variant="block"
                  />
                </Box>

                {/* Related and CTA live in same column for consistency */}
                <Box sx={{ mt: { xs: 4, sm: 5 } }}>
                  {relatedPosts.length > 0 && (
                    <LandingSectionShell
                      id={RELATED_SECTION_ID}
                      ariaLabelledby={RELATED_TITLE_ID}
                      // Inside the reading column, so it sits on the article's
                      // rhythm rather than the marketing section rhythm.
                      mb={4}
                    >
                      <Typography
                        id={RELATED_TITLE_ID}
                        variant="h2"
                        sx={{
                          fontSize: { xs: '1.25rem', sm: '1.375rem' },
                          fontWeight: 700,
                          color: colors.ink,
                          mb: 2,
                        }}
                      >
                        Related posts
                      </Typography>
                      <Stack spacing={1.5}>
                        {relatedPosts.map(post => (
                          <Card
                            key={post.slug}
                            sx={{
                              borderRadius: radii.sm,
                              border: `1px solid ${colors.border}`,
                              backgroundColor: colors.paper,
                            }}
                          >
                            <CardActionArea
                              component={RouterLink}
                              to={post.routePath}
                              sx={{ height: '100%' }}
                            >
                              <CardContent
                                sx={{
                                  p: { xs: 2, sm: 2.25 },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  textAlign: 'left',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.8125rem',
                                    color: colors.inkSubtle,
                                    mb: 0.5,
                                  }}
                                >
                                  {post.category}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    color: colors.ink,
                                  }}
                                >
                                  {post.title}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.8125rem',
                                    color: isPostUpdated(post)
                                      ? UPDATED_COLORS.text
                                      : colors.inkSubtle,
                                    fontWeight: isPostUpdated(post) ? 600 : 400,
                                    mt: 0.5,
                                  }}
                                >
                                  {isPostUpdated(post)
                                    ? `Updated ${formatBlogDate(post.updatedDate)}`
                                    : formatBlogDate(post.publishDate)}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        ))}
                      </Stack>
                    </LandingSectionShell>
                  )}

                  <BlogArticleCta
                    articleSlug={blogPost?.slug}
                    sourceLocation={undefined}
                  />
                </Box>
              </Stack>
            </Box>
          </Container>
        </Box>

        <LandingFooter />
      </Box>
    </TuitionSubjectProvider>
  );
}

export default BlogArticlePage;
